import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Text, Icon, Review as ReviewCard } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useReviewList } from "../hooks/useReviewList";
import { useReviewRatingDistribution } from "../hooks/useReviewRatingDistribution";
import { useReviewReadTracking } from "../../../../tracking";
import styles from "./Review.module.css";
import { currentLocale } from "../../../../common/i18n/locale";

const tt = (t, key, fallback) => {
  const v = t(key);
  return v && v !== key ? v : fallback;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? clamp(n, 0, 5) : 0;
};

const avg = (arr) => {
  const clean = (arr || []).map(safeNum).filter((x) => x > 0);
  if (!clean.length) return 0;
  const m = clean.reduce((a, b) => a + b, 0) / clean.length;
  return Math.round(m * 10) / 10;
};

const formatScore = (v, decimals = 2) => safeNum(v).toFixed(decimals).replace(".", ",");
const formatScore1 = (v) => safeNum(v).toFixed(1).replace(".", ",");

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  try {
    return new Intl.DateTimeFormat(currentLocale(), {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return iso.slice(0, 10);
  }
};

const makeKey = (r, idx = 0) =>
  r?.id || `${r?.email || ""}-${r?.createdDate || ""}-${r?.globalMark || ""}-${idx}`;

/** Drawer minimal (tu gardes ton pattern) */
const Drawer = ({ open, title, onClose, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);

    /* La page derrière défilait sous la molette tant que le tiroir était
       ouvert : on la fige le temps de la consultation. */
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.drawerOverlay} role="dialog" aria-modal="true">
      <button className={styles.drawerBackdrop} onClick={onClose} aria-label="Fermer" />
      <div className={styles.drawerPanel}>
        <div className={styles.drawerHeader}>
          <Text size="xl" weight="bold">
            {title}
          </Text>
          <button className={styles.drawerClose} onClick={onClose} aria-label="Fermer">
            <Icon name="iconClose" size="lg" />
          </button>
        </div>
        <div className={styles.drawerBody}>{children}</div>
      </div>
    </div>
  );
};

const Review = ({ tripPublicationId, establishmentId, embedded = false }) => {
  const { t } = useTranslation();

  const PREVIEW_SIZE = 6;
  const DRAWER_PAGE_SIZE = 10;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PREVIEW_SIZE);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("relevant");
  const [allReviews, setAllReviews] = useState([]);

  const {
    loading,
    error,
    reviews,
    globalMark,
    totalElements,
    totalPages,
    refetch,
  } = useReviewList(tripPublicationId, { currentPage: page, sizePage: pageSize });

  /* La répartition par note doit porter sur TOUS les avis, pas seulement les
     PREVIEW_SIZE chargés ici (sinon une note isolée pouvait disparaître des
     barres alors que l'en-tête annonçait la totalité). Calculée côté
     controller (QR_GET_REVIEW_RATING_DISTRIBUTION) : évite de transmettre au
     portail la liste complète des avis (commentaires, auteurs) juste pour en
     compter les notes. */
  const loadedCount = reviews?.length ?? 0;
  const needsFullList = Number(totalElements) > loadedCount;

  const { counts: ratingCounts } = useReviewRatingDistribution(tripPublicationId, {
    enabled: Boolean(tripPublicationId) && needsFullList,
  });

  useEffect(() => {
    if (!tripPublicationId) return;
    setDrawerOpen(false);
    setPage(0);
    setPageSize(PREVIEW_SIZE);
    setSearch("");
    setSort("relevant");
    setAllReviews([]);
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPublicationId]);

  useEffect(() => {
    if (!drawerOpen) return;
    if (!reviews?.length) return;

    setAllReviews((prev) => {
      const map = new Map(prev.map((r, i) => [makeKey(r, i), r]));
      reviews.forEach((r, idx) => map.set(makeKey(r, idx), r));
      return Array.from(map.values());
    });
  }, [reviews, drawerOpen]);

  const total = useMemo(() => Number(totalElements) || 0, [totalElements]);

  const previewList = useMemo(() => (reviews || []).slice(0, PREVIEW_SIZE), [reviews]);

  const showSeeAll = total > PREVIEW_SIZE;

  const canLoadMore = useMemo(() => {
    const tp = Number(totalPages) || 0;
    return tp > 0 && page < tp - 1;
  }, [page, totalPages]);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
    setSearch("");
    setSort("relevant");
    setPage(0);
    setPageSize(DRAWER_PAGE_SIZE);
    setAllReviews([]);
    setTimeout(() => refetch(), 0);
  }, [refetch]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSearch("");
    setSort("relevant");
    setPage(0);
    setPageSize(PREVIEW_SIZE);
    setAllReviews([]);
    setTimeout(() => refetch(), 0);
  }, [refetch]);

  const handleLoadMore = useCallback(() => setPage((p) => p + 1), []);

  const drawerList = useMemo(() => {
    const list = [...(allReviews || [])];
    const q = (search || "").trim().toLowerCase();

    let out = list;
    if (q) {
      out = out.filter((r) => {
        const name = `${r?.firstName ?? ""} ${r?.lastName ?? ""}`.toLowerCase();
        const c = (r?.comment ?? "").toLowerCase();
        return name.includes(q) || c.includes(q);
      });
    }

    if (sort === "recent")
      out.sort((a, b) => new Date(b?.createdDate || 0) - new Date(a?.createdDate || 0));
    if (sort === "best") out.sort((a, b) => safeNum(b?.globalMark) - safeNum(a?.globalMark));
    return out;
  }, [allReviews, search, sort]);

  // ======= Summary metrics =======
  const criteria = useMemo(() => {
    const compliance = avg((reviews || []).map((r) => r?.complianceMark));
    const service = avg((reviews || []).map((r) => r?.qualityServiceMark));
    const priceQuality = avg((reviews || []).map((r) => r?.priceQualityMark));

    return [
      {
        key: "compliance",
        label: tt(t, "Compliance", "Conformité"),
        value: compliance,
        icon: "iconCompliance",
      },
      {
        key: "service",
        label: tt(t, "ServiceQuality", "Qualité de service"),
        value: service,
        icon: "iconServiceQuality",
      },
      {
        key: "priceQuality",
        label: tt(t, "PriceQuality", "Rapport qualité/prix"),
        value: priceQuality,
        icon: "iconPriceQuality",
      },
    ];
  }, [reviews, t]);

  // Distribution Airbnb-like : compteurs serveur si disponibles, sinon repli
  // sur les avis déjà chargés (ex. avant résolution de la requête serveur).
  const distribution = useMemo(() => {
    if (ratingCounts?.length) {
      const totalLocal = ratingCounts.reduce((sum, bucket) => sum + bucket.count, 0) || 1;
      return [5, 4, 3, 2, 1].map((k) => {
        const count = ratingCounts.find((bucket) => bucket.stars === k)?.count ?? 0;
        return {
          key: k,
          label: String(k),
          count,
          pct: Math.round((count * 100) / totalLocal),
        };
      });
    }

    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    (reviews || []).forEach((r) => {
      const m = safeNum(r?.globalMark);
      const bucket = clamp(Math.round(m), 1, 5);
      counts[bucket] += 1;
    });
    const totalLocal = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
    return [5, 4, 3, 2, 1].map((k) => ({
      key: k,
      label: String(k),
      count: counts[k],
      pct: Math.round((counts[k] * 100) / totalLocal),
    }));
  }, [reviews, ratingCounts]);

  const hasSummary = total > 0;
  const { ref: reviewSectionRef } = useReviewReadTracking({
    publicationId: tripPublicationId,
    establishmentId,
    enabled: !loading && !error && total > 0,
  });

  if (!tripPublicationId) return null;

  return (
    <section ref={reviewSectionRef} className={styles.section}>
      {/* embedded : dans une colonne de page (détail du voyage), sans largeur ni marges propres */}
      <div className={embedded ? styles.containerEmbedded : styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <Text size="xl" weight="bold">
              {tt(t, "ReviewsTitle", "Avis des voyageurs")}
            </Text>
            <Text size="sm" className={styles.muted}>
              {tt(t, "ReviewsSubtitle", "Des retours authentiques pour vous aider à décider.")}
            </Text>
          </div>
          <div className={styles.summary}>
            <div className={styles.summaryHeader}>
              <div className={styles.summaryBigScore}>
                <span className={styles.bigStar} aria-hidden="true">
                  ★
                </span>
                <span className={styles.bigValue}>{formatScore(globalMark, 2)}</span>
              </div>
              <Text size="sm" className={styles.muted}>
                {total} {tt(t, "ReviewsCountLabel", "commentaires")}
              </Text>
            </div>
          </div>
        </div>

        {/* Score line (mobile only) */}
        {hasSummary && (
          <div className={styles.mobileScoreLine}>
            <span className={styles.mobileStar} aria-hidden="true">
              ★
            </span>
            <span className={styles.mobileScoreValue}>{formatScore(globalMark, 2)}</span>
            <span className={styles.mobileScoreCount}>
              {total} {tt(t, "ReviewsCountLabel", "avis")}
            </span>
          </div>
        )}

        {/* Desktop Summary */}
        {hasSummary && (
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCol}>
                <Text size="sm" weight="bold" className={styles.summaryColTitle}>
                  {tt(t, "GlobalEvaluation", "Évaluation globale")}
                </Text>

                <div className={styles.distList} aria-label="Distribution des notes">
                  {distribution.map((d) => (
                    <div key={d.key} className={styles.distRow}>
                      <span className={styles.distLabel}>{d.label}</span>
                      <span className={styles.distTrack} aria-hidden="true">
                        <span className={styles.distFill} style={{ width: `${d.pct}%` }} />
                      </span>
                      <span className={styles.distPct}>{d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {criteria.map((c) => (
                <div key={c.key} className={styles.summaryCol}>
                  <Text size="sm" weight="bold" className={styles.summaryColTitle}>
                    {c.label}
                  </Text>

                  <div className={styles.metricValueRow}>
                    <Text size="xl" weight="bold" className={styles.metricValue}>
                      {formatScore1(c.value)}
                    </Text>
                  </div>

                  <div>
                    <span className={styles.metricIconWrap} aria-hidden="true">
                      <Icon name={c.icon} size="lg" color="muted" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
        )}

        {/* States */}
        {loading && (
          <div className={styles.skeletonWrap}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className={styles.stateError}>
            <Text size="lg" weight="bold">
              {tt(t, "ReviewLoadErrorTitle", "Impossible de charger les avis")}
            </Text>
            <Text size="sm" className={styles.muted}>
              {error?.message ||
                tt(t, "ReviewLoadError", "Une erreur est survenue. Réessayez dans un instant.")}
            </Text>
            <div className={styles.stateActions}>
              <Button
                variant="primary"
                label={tt(t, "Retry", "Réessayer")}
                onClick={() => refetch()}
              />
            </div>
          </div>
        )}

        {!loading && !error && total === 0 && (
          <div className={styles.stateEmpty}>
            <Text size="lg" weight="bold">
              {tt(t, "BeFirstReviewerTitle", "Soyez le premier à laisser un avis")}
            </Text>
            <Text size="md" className={styles.muted}>
              {tt(
                t,
                "BeFirstReviewerText",
                "Après votre voyage, partagez votre expérience pour aider la communauté à choisir en toute confiance."
              )}
            </Text>
          </div>
        )}

        {/* Reviews list */}
        {!loading && !error && total > 0 && (
          <>
            <div className={styles.reviewsGrid}>
              {previewList.map((r, idx) => (
                <ReviewCard
                  key={makeKey(r, idx)}
                  variant="compact"
                  maxLines={3}
                  showScoreChip={false}
                  showScoreInline={true}
                  allowHalfStars
                  review={{
                    image: r?.picture || "",
                    name: `${r?.firstName ?? ""} ${r?.lastName ?? ""}`.trim() || "Voyageur",
                    date: formatDate(r?.createdDate),
                    rating: safeNum(r?.globalMark),
                    review: r?.comment || "",
                  }}
                />
              ))}
            </div>

            {showSeeAll && (
              <div className={styles.bottomCta}>
                <Button
                  variant="primary"
                  label={tt(t, "SeeAllReviews", `Voir tous les avis (${total})`)}
                  onClick={openDrawer}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Drawer */}
      <Drawer
        open={drawerOpen}
        title={tt(t, "AllReviewsTitle", `${total} commentaires`)}
        onClose={closeDrawer}
      >
        <div className={styles.drawerTools}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>
              <Icon name="iconSearch" size="sm" color="muted" />
            </span>
            <input
              className={styles.searchInput}
              type="search"
              placeholder={tt(t, "SearchReviews", "Rechercher dans tous les commentaires")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.sortWrap}>
            <select
              className={styles.sortSelect}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="relevant">{tt(t, "MostRelevant", "Les plus pertinents")}</option>
              <option value="recent">{tt(t, "MostRecent", "Les plus récents")}</option>
              <option value="best">{tt(t, "BestRated", "Les mieux notés")}</option>
            </select>
            <span className={styles.sortChevron}>
              <Icon name="iconChevronDown" size="sm" color="muted" />
            </span>
          </div>
        </div>

        {!loading && !error && (
          <>
            <div className={styles.drawerList}>
              {drawerList.map((r, idx) => (
                <ReviewCard
                  key={makeKey(r, idx)}
                  variant="default"
                  maxLines={8}
                  showScoreChip={false}
                  showScoreInline={false}
                  allowHalfStars
                  review={{
                    image: r?.picture || "",
                    name: `${r?.firstName ?? ""} ${r?.lastName ?? ""}`.trim() || "Voyageur",
                    date: formatDate(r?.createdDate),
                    rating: safeNum(r?.globalMark),
                    review: r?.comment || "",
                  }}
                />
              ))}
            </div>

            {canLoadMore && (
              <div className={styles.loadMore}>
                <Button
                  variant="primary"
                  label={tt(t, "portal.actions.showMore", "Afficher plus")}
                  onClick={handleLoadMore}
                />
              </div>
            )}
          </>
        )}
      </Drawer>
    </section>
  );
};

export default Review;
